import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { userAppContext } from '../../Context/Context';
import { isTrialExpired } from '../../utils/trialUtils';
import TrialExpiredModal from '../User/TrialExpiredModal';
import ReviewingApplicationModal from '../User/ReviewingApplicationModal';
import AcademicApplicationService from '../../services/AcademicApplicationService.ts';
import { fetchAuthSession } from 'aws-amplify/auth';

const GlobalTrialBanner = () => {
  const { groups, user } = userAppContext();
  const location = useLocation();
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('trial');
  const [modalDismissed, setModalDismissed] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);
  const [academicStatus, setAcademicStatus] = useState(null);
  const [trialType, setTrialType] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [hasStartedLoading, setHasStartedLoading] = useState(false);

  useEffect(() => {
    if (user && !hasChecked) {
      setModalDismissed(false);
      setHasChecked(true);
    }
  }, [user, hasChecked]);

  // Check academic status
  useEffect(() => {
    const checkAcademicStatus = async () => {
      if (user?.email && !hasStartedLoading) {
        setHasStartedLoading(true);
        setLoadingStatus(true);
        try {
          const response = await AcademicApplicationService.getAcademicStatus(user.email);

          const contextRoles = groups || [];
          const actualRoles = response.groups || [];

          const roleChanged =
            (actualRoles.includes('ROLE_ACADEMIC') && !contextRoles.includes('ROLE_ACADEMIC')) ||
            (actualRoles.includes('ROLE_STANDARD') && !contextRoles.includes('ROLE_STANDARD')) ||
            (actualRoles.includes('ROLE_ADVANCED') && !contextRoles.includes('ROLE_ADVANCED')) ||
            (contextRoles.includes('ROLE_TRIAL') && !actualRoles.includes('ROLE_TRIAL'));

          if (roleChanged) {
            const reloadKey = `role-reload-${user.email}`;
            const hasReloaded = sessionStorage.getItem(reloadKey);
            const reloadTimestamp = sessionStorage.getItem(`${reloadKey}-timestamp`);

            if (reloadTimestamp) {
              const timeSinceReload = Date.now() - parseInt(reloadTimestamp);
              if (timeSinceReload > 5 * 60 * 1000) {
                sessionStorage.removeItem(reloadKey);
                sessionStorage.removeItem(`${reloadKey}-timestamp`);
              }
            }

            if (!hasReloaded) {
              sessionStorage.setItem(reloadKey, 'true');
              sessionStorage.setItem(`${reloadKey}-timestamp`, Date.now().toString());

              try {
                await fetchAuthSession({ forceRefresh: true });
                window.location.reload();
              } catch (error) {
                console.error('Error refreshing session:', error);
                window.location.reload();
              }
              return;
            } else {
              sessionStorage.removeItem(reloadKey);
              sessionStorage.removeItem(`${reloadKey}-timestamp`);
            }
          }

          setAcademicStatus(response.status);
          setTrialType(response.trialType);
        } catch (error) {
          console.error('Error checking academic status:', error);
          setAcademicStatus(null);
          setTrialType(null);
        } finally {
          setLoadingStatus(false);
        }
      }
    };

    // Always check if we have user data
    if (user?.email) {
      checkAcademicStatus();
    }
  }, [groups, user?.email, hasStartedLoading]);

  useEffect(() => {
    if (!groups?.includes('ROLE_TRIAL') || !user?.email || academicStatus !== 'pending') {
      return;
    }

    const pollInterval = setInterval(async () => {
      try {
        const response = await AcademicApplicationService.getAcademicStatus(user.email);

        // If user is now ROLE_ACADEMIC, they were approved - reload the page
        if (response.groups?.includes('ROLE_ACADEMIC')) {
          clearInterval(pollInterval);
          window.location.reload();
        }
      } catch (error) {
        console.error('Error polling academic status:', error);
      }
    }, 30000);

    return () => clearInterval(pollInterval);
  }, [groups, user?.email, academicStatus]);

  useEffect(() => {
    // For ROLE_TRIAL users, wait until we've checked academic status
    if (groups?.includes('ROLE_TRIAL') && user?.email) {
      // If we haven't started loading yet, or we're still loading, don't show modal
      if (!hasStartedLoading || loadingStatus) {
        return;
      }
    }

    if (groups?.includes('ROLE_TRIAL') && user) {
      const expired = isTrialExpired(user);

      // Check if "reviewing" modal was dismissed and if it's been more than 7 days
      const dismissKey = 'reviewing-application-dismissed';
      const dismissTimestamp = localStorage.getItem(dismissKey);
      let reviewingModalDismissed = false;

      if (dismissTimestamp) {
        const daysSinceDismiss = (Date.now() - parseInt(dismissTimestamp)) / (1000 * 60 * 60 * 24);
        if (daysSinceDismiss < 7) {
          reviewingModalDismissed = true;
        } else {
          localStorage.removeItem(dismissKey);
        }
      }

      // If status is pending and modal not dismissed, show "reviewing" modal
      if (academicStatus === 'pending' && !reviewingModalDismissed) {
        setModalType('reviewing');
        setTimeout(() => setShowModal(true), 500);
      } else if (expired && !modalDismissed && location.pathname !== '/plans') {
        setModalType('trial');
        setTimeout(() => setShowModal(true), 500);
      } else {
        setShowModal(false);
      }
    } else {
      setShowModal(false);
    }
  }, [
    groups,
    user,
    modalDismissed,
    academicStatus,
    loadingStatus,
    hasStartedLoading,
    trialType,
    location.pathname,
  ]);

  const handleCloseModal = () => {
    if (modalType === 'reviewing') {
      const dismissKey = 'reviewing-application-dismissed';
      localStorage.setItem(dismissKey, Date.now().toString());
    }

    setModalDismissed(true);
    setShowModal(false);
  };

  if (!showModal) {
    return null;
  }

  if (modalType === 'reviewing') {
    return <ReviewingApplicationModal onClose={handleCloseModal} />;
  }

  return <TrialExpiredModal onClose={handleCloseModal} />;
};

export default GlobalTrialBanner;
